import EyeSolid from '@simplywallst/ui-core/icons/EyeSolid';
import { Button } from '@simplywallst/ui-core/next';
import { useSummaryV2 } from '@/hooks/useCompany';
import { useCompanyLimits } from '@/hooks/useCompanyLimits';
import useUser from '@/hooks/useUser';
import { useUserAccess } from '@/hooks/useUserAccess';
import { useEffect, useRef, type ReactNode } from 'react';
import throttle from 'lodash/throttle';
import Observer from '@researchgate/react-intersection-observer';
import { cn } from '@simplywallst/ui-core/next';

interface Props {
  onClick?: () => void;
  onIntersect?: () => void;
  position?: 'top' | 'bottom';
  children?: ReactNode;
}

// TODO: Split this into two components:
// - LoadSectionButtonTop
// - LoadSectionButtonBottom
// The top component will be used in the CompanyReportContents component (No scroll handling + limit description).
// The bottom component will be used in the CompanyReportContents component (Requires scroll handling + no limit description).
function ShowFullReportConfirm({
  onClick,
  onIntersect,
  position = 'top',
  children = 'View Full Report',
}: Props) {
  const { data: user } = useUser();
  const { data: userAccess, status: userAccessStatus } = useUserAccess();
  const summary = useSummaryV2();
  const companyId =
    summary.status === 'success' ? summary.companyId : undefined;
  const limits = useCompanyLimits(companyId);
  const isUnlimitedUser = userAccess.planType === 'unlimited';
  const canViewLimitDescription =
    !limits.visited &&
    user.loggedIn &&
    limits.status === 'success' &&
    userAccessStatus === 'success' &&
    !isUnlimitedUser;

  /**
   * Block reporting an intersection until the user has scrolled.
   * This avoids the next section being loaded on page load and triggering the
   * analytics on the next section / heading h2 to h3 changes etc.
   */
  const hasScrolled = useRef(false);
  useEffect(() => {
    if (!onIntersect) return;
    const scrollHandler = () => {
      hasScrolled.current = true;
    };
    const throttledHandler = throttle(scrollHandler, 100, {
      leading: true,
      trailing: true,
    });
    window.addEventListener('scroll', throttledHandler);
    return () => window.removeEventListener('scroll', throttledHandler);
  }, [onIntersect]);

  return (
    <div
      className={cn(
        'flex justify-center px-2 py-3',
        position === 'bottom' && 'lg:mb-16'
      )}
    >
      <Observer
        threshold={0}
        onChange={(e) => {
          if (e.isIntersecting && hasScrolled.current === true) {
            onIntersect?.();
          }
        }}
      >
        <div className="flex flex-col items-center justify-center gap-2">
          <Button
            variant="secondary"
            size="large"
            onClick={onClick}
            data-cy-id={
              position === 'bottom' ? 'loadNextSection' : 'loadPreviousSection'
            }
          >
            <EyeSolid />
            {children}
          </Button>
          {canViewLimitDescription && (
            <div
              data-cy-id="plan-restriction"
              className="text-balance text-center text-tiny text-solid"
            >
              {'You have viewed'}{' '}
              <span className="text-warning">{limits.views}</span>
              {` / ${limits.limit} full reports over 30 days`}
            </div>
          )}
        </div>
      </Observer>
    </div>
  );
}

export default ShowFullReportConfirm;
